"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
} from "react";
import { useCurrentLocation } from "@/hooks/use-current-location";
import { captureClientEvent } from "@/lib/analytics/client";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { claimAudioFocus, releaseAudioFocus } from "@/lib/audio-focus";
import {
  isMediaMotionActive,
  onMediaMotionSettle,
} from "@/lib/media/media-motion";

/** The video scheduler as one audio-focus holder. */
const VIDEO_AUDIO_FOCUS_ID = "video-autoplay";

type Ctx = {
  /** Whether this video element should currently be muted. */
  isMuted: (id: string) => boolean;
  /** Whether the volume control should show the user's sound preference as muted. */
  isSoundMuted: (id: string) => boolean;
  /** Toggle the mobile-wide sound preference or the desktop audio owner. */
  toggleAudio: (id: string) => void;
  register: (id: string, el: HTMLVideoElement | null) => void;
  resumeActive: () => void;
  setManualPause: (paused: boolean) => void;
};

const VideoCtx = createContext<Ctx | null>(null);

const THRESHOLD = 0.6;
/**
 * A phone plays exactly one video, so it can start on less: the most visible
 * one, once half of it is on screen, as Reels/X do. Horizontal rails
 * free-scroll, so a flick can settle with a video card at 50–60% beside an
 * image card, and at 0.6 nothing would play there until the next touch.
 */
const MOBILE_THRESHOLD = 0.5;
/**
 * Every 5%. The ratio is only reported when it crosses one of these, so a
 * coarse list left a stale value behind: a card that crossed 0.6 on its way
 * down to 0.3 kept reading 0.58 for as long as it sat there.
 */
const RATIO_STEPS = Array.from({ length: 21 }, (_, i) => i / 20);
const MOBILE_VIEWPORT_QUERY = "(max-width: 767px)";
const READY_EVENTS = ["hiive:video-source-ready", "loadedmetadata", "canplay"];
/**
 * Events that count as user activation. `pointerdown` only does for a mouse;
 * a touch activates on `touchend`/`pointerup`, so listening to `pointerdown`
 * alone never unblocked a Low Power Mode iPhone.
 */
const ACTIVATION_EVENTS = ["pointerdown", "pointerup", "touchend", "keydown"];
/**
 * How often the chosen player is checked against what it should be doing,
 * while one is chosen. See `reconcile`.
 */
const RECONCILE_MS = 500;
/** A paused owner's `pause` is answered after this, not synchronously. */
const PAUSE_RECHECK_MS = 250;
/**
 * Restarts per claim before giving up until the next claim, gesture or
 * readiness event. Bounds a player that the OS keeps refusing (Low Power
 * Mode) to a few cheap rejected play() calls rather than one every tick.
 */
const MAX_RECOVERIES = 8;
/** `HTMLMediaElement.NETWORK_EMPTY`: no source selected. */
const NETWORK_EMPTY = 0;

/**
 * Force-muted tiles that may play at once on a phone. The page-wide source
 * budget (`MAX_ATTACHED` in video-proximity) is six, so a seventh would have
 * nothing to play from anyway.
 */
const MOBILE_MUTED_PLAYERS = 6;
/**
 * How much of a card must be on screen before a drag may hand it the playing
 * slot. Well past the midpoint, so a wavering finger never flips the slot.
 */
const DRAG_HANDOFF_RATIO = 0.85;

/** Distance from the viewport centre; breaks ties between equally visible videos. */
function centreDistance(el: HTMLVideoElement) {
  const rect = el.getBoundingClientRect();
  return Math.hypot(
    rect.left + rect.width / 2 - innerWidth / 2,
    rect.top + rect.height / 2 - innerHeight / 2,
  );
}

function PathnameObserver({
  onChange,
}: {
  onChange: (pathname: string) => void;
}) {
  const { pathname } = useCurrentLocation();
  // `onChange` is a useEffectEvent, whose identity is NOT stable across
  // renders. Listing it in the deps re-runs this effect on every provider
  // render, so the pathname must be diffed explicitly — otherwise a route
  // "change" is reported continuously and resets provider state (notably
  // manualPause) between renders.
  const lastPathnameRef = useRef<string | null>(null);

  useEffect(() => {
    if (!pathname || lastPathnameRef.current === pathname) return;
    lastPathnameRef.current = pathname;
    onChange(pathname);
  }, [onChange, pathname]);

  return null;
}

function VideoAutoplayProviderContent({
  children,
}: {
  children: React.ReactNode;
}) {
  // Desktop keeps a manually selected single audio owner. Mobile instead
  // keeps one global sound preference and automatically hands audio to the
  // most-visible video as the user swipes.
  const [audioOwnerId, setAudioOwnerId] = useState<string | null>(null);
  const [mobileSoundEnabled, setMobileSoundEnabled] = useState(false);
  const [mobileAudioTargetId, setMobileAudioTargetId] = useState<string | null>(
    null,
  );
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [manualPause, setManualPauseState] = useState(false);

  const elById = useRef(new Map<string, HTMLVideoElement>());
  const idByEl = useRef(new Map<HTMLVideoElement, string>());
  const lastRatios = useRef(new Map<string, number>());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const activeIdsRef = useRef(new Set<string>());
  /** `reconcile` restarts per claim; reset only by a new claim, so a player the
   * OS keeps interrupting cannot loop. */
  const recoveries = useRef(new Map<string, number>());
  const reconcileTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  // Written eagerly rather than during render: play/pause decisions are made
  // from IntersectionObserver callbacks and awaited play() continuations, which
  // can run before React commits the state update. A ref that only catches up
  // at render time hands those callers the previous value, so a pause request
  // gets ignored and a resume request is skipped.
  const manualPauseRef = useRef(false);
  const setManualPause = useCallback((paused: boolean) => {
    manualPauseRef.current = paused;
    setManualPauseState(paused);
  }, []);
  const audioOwnerIdRef = useRef<string | null>(null);
  audioOwnerIdRef.current = audioOwnerId;
  const mobileSoundEnabledRef = useRef(false);
  mobileSoundEnabledRef.current = mobileSoundEnabled;
  const mobileAudioTargetIdRef = useRef<string | null>(null);
  mobileAudioTargetIdRef.current = mobileAudioTargetId;
  const isMobileViewportRef = useRef(false);
  isMobileViewportRef.current = isMobileViewport;

  const syncMobileAudioTarget = useCallback((ids: Iterable<string>) => {
    let nextTargetId: string | null = null;
    let nextTargetRatio = -1;

    for (const id of ids) {
      const el = elById.current.get(id);
      if (!el || el.dataset.forceMuted === "true") continue;

      const ratio = lastRatios.current.get(id) ?? 0;
      if (ratio > nextTargetRatio) {
        nextTargetId = id;
        nextTargetRatio = ratio;
      }
    }

    if (mobileAudioTargetIdRef.current === nextTargetId) return;

    mobileAudioTargetIdRef.current = nextTargetId;
    setMobileAudioTargetId(nextTargetId);
  }, []);

  // Re-runs the ownership pass without a new observer entry.
  const reselect = useEffectEvent(() => onIntersection([]));

  const pauseAll = useEffectEvent(() => {
    elById.current.forEach((el) => {
      if (!el.paused) el.pause();
    });
  });

  const tryPlay = useEffectEvent(async (id: string) => {
    if (manualPauseRef.current || document.hidden) return;
    const el = elById.current.get(id);
    if (!el) return;
    try {
      await el.play();
      // Drawer/overlay can flip manualPause while play() is in flight
      // (e.g. user pulls the hiive drawer up mid-gesture). Re-check so we
      // don't leave audio/video running under a covering surface.
      if (
        (manualPauseRef.current ||
          document.hidden ||
          !activeIdsRef.current.has(id) ||
          elById.current.get(id) !== el) &&
        !el.paused
      ) {
        el.pause();
      }
    } catch (error) {
      // iOS refuses AUDIBLE playback without a gesture (Safari, a home-screen
      // web app). The provider hands sound to each new video as you swipe, so
      // with sound on, every video after the first sat on its poster until
      // the next touch. Instagram's answer: play it muted and say so.
      if (
        (error as DOMException | undefined)?.name === "NotAllowedError" &&
        !el.muted &&
        activeIdsRef.current.has(id) &&
        elById.current.get(id) === el
      ) {
        dropToMuted();
        el.muted = true;
        captureClientEvent(ANALYTICS_EVENTS.VIDEO_AUTOPLAY_RECOVERED, {
          reason: "not_allowed_unmuted",
        });
        void tryPlay(id);
      }
      // Anything else (AbortError from a pause or a source swap, a refused
      // muted play in Low Power Mode) is retried by `reconcile`.
    }
  });

  /** Show sound as off, so the speaker icon never claims audio nobody hears. */
  const dropToMuted = () => {
    mobileSoundEnabledRef.current = false;
    setMobileSoundEnabled(false);
    audioOwnerIdRef.current = null;
    setAudioOwnerId(null);
  };

  /**
   * The chosen player is re-asserted, never fired once and forgotten.
   *
   * It used to be one play() per claim with every failure swallowed "until
   * the next user interaction" — so any pause the provider did not ask for
   * (an iOS audio interruption, a Low Power Mode refusal, a play() aborted by
   * a pause or a source swap on a slow link) left a fully visible video on
   * its poster until the viewer touched the screen, when the gesture
   * listener's play() finally started it. Now the owner's own `pause` event
   * and a light tick while an owner exists both restart it.
   */
  const reconcile = useEffectEvent(() => {
    if (manualPauseRef.current || document.hidden) return;
    for (const id of activeIdsRef.current) {
      const el = elById.current.get(id);
      if (!el?.paused) continue;
      // No source yet (cold, or released by the proximity scheduler).
      // HlsVideo announces one with `hiive:video-source-ready`, and a play()
      // now would only be aborted by its load().
      if (el.networkState === NETWORK_EMPTY) continue;
      const attempts = recoveries.current.get(id) ?? 0;
      if (attempts >= MAX_RECOVERIES) continue;
      recoveries.current.set(id, attempts + 1);
      if (attempts === 0)
        captureClientEvent(ANALYTICS_EVENTS.VIDEO_AUTOPLAY_RECOVERED, {
          reason: "paused_owner",
          ready_state: el.readyState,
          muted: el.muted,
        });
      void tryPlay(id);
    }
  });

  /**
   * On a phone only ONE sound-capable video plays: the most visible, then the
   * one already playing, then the one nearest the centre. Two carousel rows
   * on screen used to run two or three players at once, splitting a cellular
   * link and the decoder between them — the stutters and mid-clip freezes.
   * Feeds like Reels play one; the rest sit on their first frame, which
   * matches the poster.
   *
   * Force-muted tiles (the browse grids on the hub tabs) are exempt: they
   * never own audio, and a grid where one tile moves reads as broken. Every
   * visible one plays, up to `MOBILE_MUTED_PLAYERS`.
   */
  const pickPlaying = (visibleIds: Set<string>) => {
    if (!isMobileViewportRef.current || visibleIds.size <= 1) return visibleIds;
    const current = activeIdsRef.current;
    const ranked = (ids: string[]) =>
      ids
        .map((id) => {
          const el = elById.current.get(id);
          const score: [number, number, number] = el
            ? [
                lastRatios.current.get(id) ?? 0,
                current.has(id) ? 1 : 0,
                -centreDistance(el),
              ]
            : [-1, 0, 0];
          return { id, score };
        })
        .sort(
          (a, b) =>
            b.score[0] - a.score[0] ||
            b.score[1] - a.score[1] ||
            b.score[2] - a.score[2],
        )
        .map(({ id }) => id);
    const muted: string[] = [];
    const audible: string[] = [];
    for (const id of visibleIds) {
      const el = elById.current.get(id);
      if (!el) continue;
      (el.dataset.forceMuted === "true" ? muted : audible).push(id);
    }
    const [best] = ranked(audible);
    return new Set([
      ...(best ? [best] : []),
      ...ranked(muted).slice(0, MOBILE_MUTED_PLAYERS),
    ]);
  };

  const isDecisiveHandOff = (
    previousIds: Set<string>,
    nextIds: Set<string>,
  ) => {
    if (nextIds.size === 0) return false;
    for (const id of nextIds) {
      if (previousIds.has(id)) continue;
      if ((lastRatios.current.get(id) ?? 0) < DRAG_HANDOFF_RATIO) return false;
    }
    return true;
  };

  /**
   * Publishes which players the provider owns, for HlsVideo: its warm-up
   * pauses a neighbour on first frame, and must not pause one that became
   * the real player while its warm-up play() was still pending.
   */
  const markActive = (previousIds: Set<string>, nextIds: Set<string>) => {
    for (const id of previousIds) {
      if (!nextIds.has(id))
        delete elById.current.get(id)?.dataset.autoplayActive;
    }
    for (const id of nextIds) {
      if (!previousIds.has(id)) recoveries.current.delete(id);
      const el = elById.current.get(id);
      if (el) el.dataset.autoplayActive = "true";
    }
    // Tick only while something is supposed to be playing.
    if (nextIds.size && !reconcileTimer.current)
      reconcileTimer.current = setInterval(() => reconcile(), RECONCILE_MS);
    else if (!nextIds.size && reconcileTimer.current) {
      clearInterval(reconcileTimer.current);
      reconcileTimer.current = null;
    }
  };

  const onIntersection = useEffectEvent(
    (entries: IntersectionObserverEntry[]) => {
      for (const entry of entries) {
        const id = idByEl.current.get(entry.target as HTMLVideoElement);
        if (!id) continue;
        lastRatios.current.set(id, entry.intersectionRatio);
      }
      const dragging = isMediaMotionActive("drag");

      const visibleIds = new Set<string>();
      const threshold = isMobileViewportRef.current
        ? MOBILE_THRESHOLD
        : THRESHOLD;
      for (const [id, ratio] of lastRatios.current.entries()) {
        if (ratio >= threshold) {
          visibleIds.add(id);
        }
      }

      const previousIds = activeIdsRef.current;
      const playingIds = pickPlaying(visibleIds);
      // A carousel under the finger hands the slot over only to a card that
      // is decisively in view. Following the plain "most visible" rule there
      // flipped players back and forth as a finger wavered around the midpoint
      // — a decoder start and stop per wobble. A card dragged fully in and
      // held still plays without waiting for the lift; everything else is
      // re-picked the moment the finger lifts, so a flung card starts during
      // its glide and is already playing when it lands.
      if (dragging && !isDecisiveHandOff(previousIds, playingIds)) return;
      // Publish ownership before play(): source attachment can synchronously
      // emit readiness, and pending play promises must see the current owner.
      activeIdsRef.current = playingIds;
      markActive(previousIds, playingIds);
      // Play newly active videos
      for (const id of playingIds) {
        if (!previousIds.has(id)) {
          void tryPlay(id);
        }
      }

      // Pause videos that lost the slot
      for (const id of previousIds) {
        if (!playingIds.has(id)) {
          const el = elById.current.get(id);
          if (el && !el.paused) el.pause();
        }
      }

      if (isMobileViewportRef.current) {
        syncMobileAudioTarget(playingIds);
      }
    },
  );

  const handlePathnameChange = useEffectEvent((_pathname: string) => {
    setManualPause(false);
    pauseAll();
    markActive(activeIdsRef.current, new Set());
    activeIdsRef.current = new Set();
    // Re-arm the observer. A client-side navigation does not change the
    // intersection of elements that survive it, so no callback would follow
    // the pauseAll above and everything still on screen would stay frozen
    // until the user scrolled. Re-observing a target queues a fresh initial
    // callback, which routes back through onIntersection and restarts what is
    // actually visible.
    const observer = observerRef.current;
    if (!observer) return;
    elById.current.forEach((el) => {
      observer.unobserve(el);
      observer.observe(el);
    });
  });

  // `onIntersection` is a useEffectEvent, so capturing it once here is safe —
  // the returned function always runs the latest closure.
  const ensureObserver = useCallback(() => {
    if (observerRef.current) return observerRef.current;
    observerRef.current = new IntersectionObserver(onIntersection, {
      threshold: RATIO_STEPS,
    });
    return observerRef.current;
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_VIEWPORT_QUERY);

    function updateViewportMode() {
      isMobileViewportRef.current = mediaQuery.matches;
      setIsMobileViewport(mediaQuery.matches);

      if (mediaQuery.matches) {
        syncMobileAudioTarget(activeIdsRef.current);
      }
      // The playing budget differs between the two modes.
      reselect();
    }

    updateViewportMode();
    mediaQuery.addEventListener("change", updateViewportMode);
    return () => mediaQuery.removeEventListener("change", updateViewportMode);
  }, [syncMobileAudioTarget]);

  /**
   * Must stay referentially stable. Consumers pass `register` straight into a
   * `ref`, so a new identity each render makes React detach and re-attach that
   * ref on every commit — which runs the `!el` branch below and therefore
   * pauses the video, drops it from the observer, and re-observes it. Measured
   * before this was stable: one tap on one card's volume button re-registered
   * all 11 videos on /hiives, because the unmemoized context value re-rendered
   * every consumer.
   */
  const readyHandlers = useRef(new Map<string, () => void>());
  const pauseHandlers = useRef(new Map<string, () => void>());
  const register = useCallback(
    (id: string, el: HTMLVideoElement | null) => {
      const observer = ensureObserver();

      if (!el) {
        const prevEl = elById.current.get(id);
        if (prevEl) {
          // Explicitly pause the video before unregistering
          if (!prevEl.paused) {
            prevEl.pause();
          }
          const handler = readyHandlers.current.get(id);
          if (handler) {
            for (const event of READY_EVENTS)
              prevEl.removeEventListener(event, handler);
          }
          readyHandlers.current.delete(id);
          const onPause = pauseHandlers.current.get(id);
          if (onPause) prevEl.removeEventListener("pause", onPause);
          pauseHandlers.current.delete(id);
          observer.unobserve(prevEl);
          idByEl.current.delete(prevEl);
          delete prevEl.dataset.autoplayActive;
        }
        elById.current.delete(id);
        lastRatios.current.delete(id);
        recoveries.current.delete(id);
        // The player that held the phone's single slot unmounted (a far
        // banner slide, a card filtered away): hand the slot on.
        if (activeIdsRef.current.delete(id)) queueMicrotask(() => reselect());
        // The audio owner unmounted; fall back to everything muted. Deferred a
        // microtask because a ref can still detach+reattach within one commit
        // (a remount, or a caller that has not stabilized its ref callback) —
        // resetting synchronously would instantly undo the unmute the user
        // just clicked.
        if (audioOwnerIdRef.current === id) {
          queueMicrotask(() => {
            if (!elById.current.has(id) && audioOwnerIdRef.current === id) {
              setAudioOwnerId((current) => (current === id ? null : current));
            }
          });
        }
        if (mobileAudioTargetIdRef.current === id) {
          queueMicrotask(() => {
            if (
              !elById.current.has(id) &&
              mobileAudioTargetIdRef.current === id
            ) {
              syncMobileAudioTarget(activeIdsRef.current);
            }
          });
        }
        return;
      }

      // Initialize element
      const isForceMuted = el.dataset.forceMuted === "true";
      el.muted = isForceMuted
        ? true
        : isMobileViewportRef.current
          ? !mobileSoundEnabledRef.current ||
            mobileAudioTargetIdRef.current !== id
          : audioOwnerIdRef.current !== id;
      el.playsInline = true;
      el.autoplay = false;
      el.loop = true;
      el.controls = false;

      const onReady = () => {
        if (activeIdsRef.current.has(id)) void tryPlay(id);
      };
      readyHandlers.current.set(id, onReady);
      for (const event of READY_EVENTS) el.addEventListener(event, onReady);
      // A pause the provider did not ask for: answered shortly, not on the
      // next touch. Its own pauses only ever hit players it no longer owns.
      const onPause = () => {
        if (activeIdsRef.current.has(id))
          setTimeout(() => reconcile(), PAUSE_RECHECK_MS);
      };
      pauseHandlers.current.set(id, onPause);
      el.addEventListener("pause", onPause);
      elById.current.set(id, el);
      idByEl.current.set(el, id);
      lastRatios.current.set(id, 0);
      observer.observe(el);
    },
    [ensureObserver, syncMobileAudioTarget],
  );

  // Keep DOM media state in sync. Desktop uses the manually selected owner;
  // mobile automatically transfers audio to the most-visible video while the
  // user's global sound preference remains enabled.
  useEffect(() => {
    elById.current.forEach((el, id) => {
      el.muted =
        el.dataset.forceMuted === "true" ||
        (isMobileViewport
          ? !mobileSoundEnabled || mobileAudioTargetId !== id
          : audioOwnerId !== id);
    });
  }, [audioOwnerId, isMobileViewport, mobileAudioTargetId, mobileSoundEnabled]);

  useEffect(() => onMediaMotionSettle(() => reselect(), "drag"), []);

  // Global resume on first user gesture (iOS LPM workaround)
  useEffect(() => {
    function onInteract() {
      for (const id of activeIdsRef.current) {
        void tryPlay(id);
      }
    }
    for (const event of ACTIVATION_EVENTS)
      window.addEventListener(event, onInteract, { capture: true });
    return () => {
      for (const event of ACTIVATION_EVENTS)
        window.removeEventListener(event, onInteract, { capture: true });
    };
  }, []);

  // Pause in background tabs
  useEffect(() => {
    function onVis() {
      if (document.hidden) {
        pauseAll();
      } else {
        for (const id of activeIdsRef.current) {
          void tryPlay(id);
        }
      }
    }
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  useEffect(
    () => () => {
      if (reconcileTimer.current) clearInterval(reconcileTimer.current);
      reconcileTimer.current = null;
    },
    [],
  );

  // Handle manual pause state changes
  useEffect(() => {
    if (manualPause) {
      pauseAll();
    } else {
      for (const id of activeIdsRef.current) {
        void tryPlay(id);
      }
    }
  }, [manualPause]);

  // Turning sound on takes app-wide audio focus (src/lib/audio-focus.ts), so a
  // song preview playing elsewhere stops; a preview starting later calls
  // `release` here, which mutes every video.
  const releaseVideoAudio = useCallback(() => {
    mobileSoundEnabledRef.current = false;
    setMobileSoundEnabled(false);
    audioOwnerIdRef.current = null;
    setAudioOwnerId(null);
  }, []);

  const toggleAudio = useCallback(
    (id: string) => {
      if (isMobileViewportRef.current) {
        const next = !mobileSoundEnabledRef.current;
        mobileSoundEnabledRef.current = next;
        setMobileSoundEnabled(next);
        mobileAudioTargetIdRef.current = id;
        setMobileAudioTargetId(id);
        if (next) {
          claimAudioFocus({
            id: VIDEO_AUDIO_FOCUS_ID,
            release: releaseVideoAudio,
          });
        } else {
          releaseAudioFocus(VIDEO_AUDIO_FOCUS_ID);
        }
        return;
      }

      const next = audioOwnerIdRef.current === id ? null : id;
      audioOwnerIdRef.current = next;
      setAudioOwnerId(next);
      if (next) {
        claimAudioFocus({
          id: VIDEO_AUDIO_FOCUS_ID,
          release: releaseVideoAudio,
        });
      } else {
        releaseAudioFocus(VIDEO_AUDIO_FOCUS_ID);
      }
    },
    [releaseVideoAudio],
  );

  const resumeActive = useCallback(() => {
    for (const id of activeIdsRef.current) {
      void tryPlay(id);
    }
  }, []);

  // With sound off, the mobile audio target follows every hand-off of the
  // playing slot but mutes nothing, so it must not re-render every consumer.
  const audibleTargetId = mobileSoundEnabled ? mobileAudioTargetId : null;
  /**
   * Memoized deliberately. Every video card on the page consumes this context,
   * so a fresh object literal per render re-rendered all of them on any audio
   * or viewport state change — and on mobile `syncMobileAudioTarget` fires that
   * on every carousel snap. The value still changes when the mute state changes
   * (`isMuted` closes over it), which is correct; what it no longer does is
   * change for unrelated reasons.
   */
  const value = useMemo<Ctx>(
    () => ({
      isMuted: (id: string) =>
        isMobileViewport
          ? !mobileSoundEnabled || audibleTargetId !== id
          : audioOwnerId !== id,
      isSoundMuted: (id: string) =>
        isMobileViewport ? !mobileSoundEnabled : audioOwnerId !== id,
      toggleAudio,
      register,
      resumeActive,
      setManualPause,
    }),
    [
      audibleTargetId,
      audioOwnerId,
      isMobileViewport,
      mobileSoundEnabled,
      register,
      resumeActive,
      setManualPause,
      toggleAudio,
    ],
  );

  return (
    <VideoCtx.Provider value={value}>
      {children}
      <PathnameObserver onChange={handlePathnameChange} />
    </VideoCtx.Provider>
  );
}

export function VideoAutoplayProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <VideoAutoplayProviderContent>{children}</VideoAutoplayProviderContent>
  );
}

export function useVideoAutoplay() {
  const ctx = useContext(VideoCtx);
  if (!ctx)
    throw new Error(
      "useVideoAutoplay must be used within VideoAutoplayProvider",
    );
  return ctx;
}
